import { v4 } from "uuid";

import { recordReload } from "@/services/ReloadDiagnostics";
import { useCaseStore } from "@/stores/case-store";
import { useVoiceMachineStore } from "@/stores/voice-machine-store";
import { useIsPopout } from "@/hooks/use-is-popout";

import type { RouteLocationResolved, Router } from "vue-router";

// Offsite (Teams/M365), every agent citation link opens a new tab: the host's openLink takes no named
// target, and COOP would clear a window name anyway. Instead, a tab opened from one of those links
// hands its URL to the viewer tab the user last had active and closes itself, so clicking through
// findings keeps reusing one tab.
const CHANNEL_NAME = "unity-viewer-tabs";

// How long a new tab waits for an existing one to take its link before loading it itself, e.g. when
// none is open or the browser has frozen it in the background. Runs alongside loadConfig.
const REPLY_TIMEOUT_MS = 400;

type TabMessage =
  | { type: "active"; tabId: string }
  | { type: "open"; requestId: string; url: string }
  | { type: "opened"; requestId: string };

/**
 * For a tab opened from an agent link (those carry conversationId, see UnityAgent's
 * ViewerLinkProvider), offers its URL to an existing viewer tab. Resolves true if one took it, in
 * which case this tab has been closed, or told the user where the link went if close() was refused.
 */
export async function handOffToActiveTab(): Promise<boolean> {
  if (!new URLSearchParams(location.search).has("conversationId")) return false;

  const channel = new BroadcastChannel(CHANNEL_NAME);
  const requestId = v4();
  const accepted = await new Promise<boolean>((resolve) => {
    const timeout = window.setTimeout(() => resolve(false), REPLY_TIMEOUT_MS);
    channel.onmessage = ({ data }: MessageEvent<TabMessage>) => {
      if (data.type !== "opened" || data.requestId !== requestId) return;
      window.clearTimeout(timeout);
      resolve(true);
    };
    channel.postMessage({ type: "open", requestId, url: location.href } satisfies TabMessage);
  });
  channel.close();

  if (!accepted) return false;

  // Browsers only allow close() on tabs opened by script, which is how hosts open links.
  window.close();
  document.getElementById("app")!.textContent = "This finding has opened in your existing Unity tab.";
  return true;
}

/** Takes agent links from newly opened tabs while this tab is the one the user last loaded or focused. */
export function listenForHandOffs(router: Router) {
  if (useIsPopout()) return;

  const tabId = v4();
  const channel = new BroadcastChannel(CHANNEL_NAME);
  let active = true;

  function claimActive() {
    active = true;
    channel.postMessage({ type: "active", tabId } satisfies TabMessage);
  }

  channel.onmessage = ({ data }: MessageEvent<TabMessage>) => {
    // A channel never receives its own messages, so any "active" means another tab took over.
    if (data.type === "active") {
      active = false;
      return;
    }
    if (data.type !== "open" || !active) return;

    const url = new URL(data.url);
    if (url.origin !== location.origin) return;

    const target = router.resolve(`${url.pathname}${url.search}`);
    if (!target.matched.some((record) => record.meta.isCaseReference === true)) return;

    channel.postMessage({ type: "opened", requestId: data.requestId } satisfies TabMessage);
    void openHandedOffLink(router, target, url.href);
  };

  claimActive();
  window.addEventListener("focus", claimActive);
}

// Only the case already open, linked to the same conversation, can switch in place: the case load
// (and the conversation back-link it carries) only runs when Case.vue mounts, so anything else is a
// full load of the link in this tab instead.
async function openHandedOffLink(router: Router, target: RouteLocationResolved, href: string) {
  const caseStore = useCaseStore();
  const { conversationId, finding, ...query } = target.query;
  const current = router.currentRoute.value;

  if (
    current.params.brand !== target.params.brand ||
    caseStore.selectedCaseName !== target.params.casename ||
    conversationId !== caseStore.backLinkedConversationId
  ) {
    // Agent links carry no lens, so keep the one this tab is on rather than the session's last used.
    const url = new URL(href);
    const lens = current.query.lens;
    if (current.params.brand === target.params.brand && typeof lens === "string" && !url.searchParams.has("lens")) {
      url.searchParams.set("lens", lens);
    }
    recordReload("viewer-tab-handoff");
    location.assign(url.href);
    return;
  }

  // Mirrors AgentAssistantChat's goToSession/goToCitation, the onsite equivalents of these links.
  if (typeof finding === "string") caseStore.toolModeFindingReference = finding;

  const { casefile, startTime, endTime } = target.params;
  if (typeof casefile !== "string") return;

  await router.push({ path: target.path, query, force: true });
  if (target.name === "casefiletimerange") {
    useVoiceMachineStore().setTimeRange("agent-citation", casefile, Number(startTime), Number(endTime));
  }
}
